import React, { useState, useEffect } from 'react';
import { Spinner, Alert } from 'reactstrap';
import { FileText, Download } from 'lucide-react';
import { API_URL } from '../utils/Constants/URLS';
import PageHeader from '../utils/PageHeader';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const ViewDebitNotes = () => {
    const [debitNotes, setDebitNotes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [downloadingId, setDownloadingId] = useState(null);

    const token = localStorage.getItem('token');

    useEffect(() => {
        fetchDebitNotes();
    }, []);

    const fetchDebitNotes = async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await fetch(`${API_URL}debit-notes`, {
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                }
            });
            const result = await res.json();
            if (!res.ok || result.error) {
                throw new Error(result.message || 'Failed to fetch debit notes');
            }
            const items = result.data?.docs || result.data || [];
            // Sort by latest first
            items.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
            setDebitNotes(items);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleDownloadPdf = async (dn) => {
        setDownloadingId(dn._id);
        try {
            const gstReq = dn.gstzenRequestPayload || {};
            const seller = gstReq.SellerDtls || {};
            const buyer = gstReq.BuyerDtls || {};
            const ship = gstReq.ShipDtls || gstReq.DispDtls || {};
            const val = gstReq.ValDtls || {};
            const items = Array.isArray(gstReq.ItemList) ? gstReq.ItemList : [];

            const totalAmount = items.reduce((sum, it) => {
                const qty = Number(it.Qty || 0);
                const rate = Number(it.UnitPrice || 0);
                const baseAmount = qty * rate;
                const discount = Number(it.Discount || 0);
                return sum + (baseAmount - discount);
            }, 0);

            const hasDiscount = items.some(it => Number(it?.Discount || 0) > 0);
            const poNumber = dn.purchaseOrder?.poNumber || '-';

            const billingAdd = (buyer?.Loc && buyer?.Pin) ? `${buyer.Loc}` : '-';
            const billingPin = buyer?.Pin || '-';
            const shippingAdd = `${ship?.Addr1 || ''}` || '-';
            const shippingPin = ship?.Pin || '-';
            const poDateRaw = dn.purchaseOrder?.orderPlacedDate || dn.purchaseOrder?.createdAt;
            const poDate = poDateRaw ? new Date(poDateRaw).toLocaleDateString('en-GB') : '-';

            const irn = dn.irn || '-';
            const ackNo = dn.ackNo || '-';

            const formatDate = (date) => {
                if (!date || date === '-') return '-';
                return new Date(date).toLocaleDateString('en-GB', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric',
                });
            };

            const ackDateRaw = dn.ackDate || '-';
            const ackDate = formatDate(ackDateRaw);
            const isIGST = Number(val?.IgstVal || dn?.igstAmount || 0) > 0;

            const money = n => (Number(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

            const numberToWords = num => {
                const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
                const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
                const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
                const convertLessThanThousand = n => {
                    if (n === 0) return '';
                    if (n < 10) return ones[n];
                    if (n < 20) return teens[n - 10];
                    if (n < 100) return (tens[Math.floor(n / 10)] + (n % 10 !== 0 ? ' ' + ones[n % 10] : ''));
                    return (ones[Math.floor(n / 100)] + ' Hundred' + (n % 100 !== 0 ? ' ' + convertLessThanThousand(n % 100) : ''));
                }
                const rupees = Math.floor(num);
                const paise = Math.round((num - rupees) * 100);
                if (rupees === 0 && paise === 0) return 'Zero Rupees Only';
                const crore = Math.floor(rupees / 10000000);
                const lakh = Math.floor((rupees % 10000000) / 100000);
                const thousand = Math.floor((rupees % 100000) / 1000);
                const hundred = rupees % 1000;
                let result = '';
                if (crore > 0) result += convertLessThanThousand(crore) + ' Crore ';
                if (lakh > 0) result += convertLessThanThousand(lakh) + ' Lakh ';
                if (thousand > 0) result += convertLessThanThousand(thousand) + ' Thousand ';
                if (hundred > 0) result += convertLessThanThousand(hundred);
                let finalResult = result.trim() ? result.trim() + ' Rupees' : 'Zero Rupees';
                if (paise > 0) finalResult += ' and ' + convertLessThanThousand(paise) + ' Paise';
                return finalResult + ' Only';
            };

            let qrImg = '';
            if (dn.signedQRCode) {
                try {
                    const QRCode = await import('qrcode');
                    qrImg = await QRCode.toDataURL(dn.signedQRCode, { margin: 0, scale: 4 });
                } catch (e) {
                    console.log('QRCode error', e);
                }
            }

            const groupedByHsn = items.reduce((acc, it) => {
                const hsn = it.HsnCd ? String(it.HsnCd).trim() : '-';
                if (!acc[hsn]) {
                    acc[hsn] = { hsnCode: hsn, taxableValue: 0, cgstAmt: 0, sgstAmt: 0, igstAmt: 0, gstRate: it.GstRt || 0 };
                }
                const itemTotal = it.AssAmt;
                acc[hsn].taxableValue += itemTotal;
                acc[hsn].cgstAmt += Number(it.CgstAmt || 0);
                acc[hsn].sgstAmt += Number(it.SgstAmt || 0);
                acc[hsn].igstAmt += Number(it.IgstAmt || 0);
                return acc;
            }, {});

            const hsnGroups = Object.values(groupedByHsn);
            const netTotal = totalAmount + (isIGST ? Number(val?.IgstVal || dn?.igstAmount || 0) : Number(val?.SgstVal || dn?.sgstAmount || 0) + Number(val?.CgstVal || dn?.cgstAmount || 0));

            const generateHeader = () => `
            <div style="border:1px solid #000; background: white;">
              <div style="text-align:center; padding:10px 20px; margin-bottom:5px">
                <center><h2 style="margin:5px 0">Debit Note</h2></center>
              </div>
              
              <div style="display:flex; border-bottom:1px solid #000; border-top:1px solid #000;">
                <div style="width:40%; padding:8px; border-right:1px solid #000">
                  <div style="font-size:12px; font-weight:bold">${String(seller.LglNm || '-')}</div>
                  <div style="font-size:12px">${String(seller.Addr1 || '')}</div>
                  <div style="font-size:12px">GSTIN: ${String(seller.Gstin || '-')}</div>
                  <div style="font-size:12px">CIN: U74140WB2011PTC159722</div>
                  <div style="font-size:12px">UDYAM Registration no: UDYAM-WB-09-0000238</div>
                </div>
                <div style="width:60%; padding:8px">
                  <table style="width:100%; border-collapse:collapse">
                    <tr><td style="font-weight:bold; padding:2px 4px; font-size:12px">Debit Note No.</td><td style="padding:2px 4px; font-size:12px">${String(gstReq?.DocDtls?.No || dn.debitNoteNo || '-')}</td></tr>
                    <tr><td style="font-weight:bold; padding:2px 4px; font-size:12px">Debit Note Date</td><td style="padding:2px 4px; font-size:12px">${String(gstReq?.DocDtls?.Dt || new Date(dn.createdAt).toLocaleDateString('en-GB') || '-')}</td></tr>
                    <tr><td style="font-weight:bold; padding:2px 4px; font-size:12px">PO No.</td><td style="padding:2px 4px; font-size:12px">${String(poNumber || '-')}</td></tr>
                    <tr><td style="font-weight:bold; padding:2px 4px; font-size:12px">PO Date</td><td style="padding:2px 4px; font-size:12px">${poDate}</td></tr>
                    <tr><td style="font-weight:bold; padding:2px 4px; font-size:12px">Other References</td><td style="padding:2px 4px; font-size:12px">${String(dn.otherReferences || '-')}</td></tr>
                  </table>
                </div>
              </div>
              <div style="display:flex; border-bottom:1px solid #000">
                <div style="width:50%; padding:8px">
                  <div style="font-size:12px; font-weight:bold">Bill To</div>
                  <div style="font-size:12px">${String(buyer.LglNm || '-')}</div>
                  <div style="font-size:12px">${String(buyer.Addr1 || '')}</div>
                  <div style="font-size:12px">Pin - ${String(billingPin || '-')}</div>
                  <div style="font-size:12px">State Code: ${String(buyer.Stcd || '-')}</div>
                  <div style="font-size:12px">GSTIN: ${String(buyer.Gstin || '-')}</div>
                </div>
                <div style="width:50%; padding:8px; border-left:1px solid #000">
                  <div style="font-size:12px; font-weight:bold">Ship To</div>
                  <div style="font-size:12px">${String(ship.LglNm || '-')}</div>
                  <div style="font-size:12px">${String(ship.Addr1 || '')}</div>
                  <div style="font-size:12px">Pin - ${String(shippingPin || '-')}</div>
                  <div style="font-size:12px">State Code: ${String(ship.Stcd || '-')}</div>
                  <div style="font-size:12px">GSTIN: ${String(ship.Gstin || '-')}</div>
                </div>
              </div>
            </div>`;

            const container = document.createElement('div');
            container.style.cssText = 'position: absolute; left: -9999px; top: 0; width: 794px; padding: 0; margin: 0; background: white; box-sizing: border-box;';
            document.body.appendChild(container);

            try {
                const { jsPDF } = window.jspdf || (await import('jspdf'));
                const pdf = new jsPDF('p', 'mm', 'a4');
                const pageHeight = 297; const pageWidth = 210; const margin = 5;
                const itemsPerPage = 15;
                const totalPages = Math.ceil(items.length / itemsPerPage) || 1;

                for (let pageNum = 0; pageNum < totalPages; pageNum++) {
                    const startIdx = pageNum * itemsPerPage;
                    const endIdx = Math.min(startIdx + itemsPerPage, items.length);
                    const pageItems = items.slice(startIdx, endIdx);
                    const isLastPage = pageNum === totalPages - 1;

                    const pageHTML = `
                      <div style="font-family: Arial; width: 100%; background: white; padding: 0; margin: 0; box-sizing: border-box;">
                        ${generateHeader()}
                        <div style="border:1px solid #000; border-top:none; padding:0; margin:0;">
                          <table style="width:100%; border-collapse:collapse;">
                            <thead>
                              <tr>
                                <th style="padding:8px; font-size:12px; text-align:center; border-right:1px solid #000; border-bottom:1px solid #000;">Sl</th>
                                <th style="padding:8px; font-size:12px; text-align:center; border-right:1px solid #000; border-bottom:1px solid #000;">Description</th>
                                <th style="padding:8px; font-size:12px; text-align:center; border-right:1px solid #000; border-bottom:1px solid #000;">UOM</th>
                                <th style="padding:8px; font-size:12px; text-align:center; border-right:1px solid #000; border-bottom:1px solid #000;">HSN</th>
                                <th style="padding:8px; font-size:12px; text-align:center; border-right:1px solid #000; border-bottom:1px solid #000;">GST%</th>
                                <th style="padding:8px; font-size:12px; text-align:center; border-right:1px solid #000; border-bottom:1px solid #000;">Qty</th>
                                <th style="padding:8px; font-size:12px; text-align:center; border-right:1px solid #000; border-bottom:1px solid #000;">Rate</th>
                                ${hasDiscount ? `<th style="padding:8px; font-size:12px; text-align:center; border-right:1px solid #000; border-bottom:1px solid #000;">Disc</th>` : ''}
                                <th style="padding:8px; font-size:12px; text-align:center; border-bottom:1px solid #000;">Amount</th>
                              </tr>
                            </thead>
                            <tbody>
                              ${pageItems.map((it, idx) => {
                        const globalIdx = startIdx + idx;
                        const discount = Number(it.Discount || 0);
                        return `
                                  <tr style="${idx === pageItems.length - 1 ? 'border-bottom:1px solid #000;' : ''}">
                                    <td style="padding:4px; font-size:12px; text-align:center; border-right:1px solid #000;">${globalIdx + 1}</td>
                                    <td style="padding:4px; font-size:12px; border-right:1px solid #000;">${String(it.PrdDesc || '-')}</td>
                                    <td style="padding:4px; font-size:12px; text-align:center; border-right:1px solid #000;">${String(it.Unit || '-')}</td>
                                    <td style="padding:4px; font-size:12px; text-align:center; border-right:1px solid #000;">${String(it.HsnCd || '-')}</td>
                                    <td style="padding:4px; font-size:12px; text-align:center; border-right:1px solid #000;">${String(it.GstRt || 0)}</td>
                                    <td style="padding:4px; font-size:12px; text-align:center; border-right:1px solid #000;">${Number(it.Qty || 0)}</td>
                                    <td style="padding:4px; font-size:12px; text-align:right; border-right:1px solid #000;">${money(it.UnitPrice)}</td>
                                    ${hasDiscount ? `<td style="padding:4px; font-size:12px; text-align:right; border-right:1px solid #000;">${money(discount)}</td>` : ''}
                                    <td style="padding:4px; font-size:12px; text-align:right;">${money(it.AssAmt)}</td>
                                  </tr>
                                `;
                    }).join('')}
                              
                              ${isLastPage ? `
                                <tr>
                                  <td colspan="${hasDiscount ? 8 : 7}" style="padding:4px; font-size:12px; text-align:right; font-weight:bold;">Total</td>
                                  <td style="padding:4px; font-size:12px; text-align:right; font-weight:bold;">${money(val?.AssVal || totalAmount)}</td>
                                </tr>
                                ${isIGST
                                ? `<tr><td colspan="${hasDiscount ? 8 : 7}" style="padding:4px; font-size:12px; text-align:right; font-weight:bold;">IGST</td><td style="padding:4px; font-size:12px; text-align:right; font-weight:bold;">${money(val?.IgstVal || dn?.igstAmount || 0)}</td></tr>`
                                : `<tr><td colspan="${hasDiscount ? 8 : 7}" style="padding:4px; font-size:12px; text-align:right; font-weight:bold;">CGST</td><td style="padding:4px; font-size:12px; text-align:right; font-weight:bold;">${money(val?.CgstVal || dn?.cgstAmount || 0)}</td></tr>
                                     <tr><td colspan="${hasDiscount ? 8 : 7}" style="padding:4px; font-size:12px; text-align:right; font-weight:bold;">SGST</td><td style="padding:4px; font-size:12px; text-align:right; font-weight:bold;">${money(val?.SgstVal || dn?.sgstAmount || 0)}</td></tr>`
                            }
                                <tr>
                                  <td colspan="${hasDiscount ? 8 : 7}" style="padding:4px; font-size:12px; text-align:right; font-weight:bold;">Net Total</td>
                                  <td style="padding:4px; font-size:12px; text-align:right; font-weight:bold;">${money(Math.round(netTotal))}</td>
                                </tr>
                                ${(() => {
                                const rounded = Math.round(netTotal);
                                const diff = rounded - netTotal;
                                if (diff === 0) return '';
                                return `
                                    <tr>
                                      <td colspan="${hasDiscount ? 8 : 7}" style="padding:4px; font-size:12px; text-align:right; font-weight:bold;">Rounded</td>
                                      <td style="padding:4px; font-size:12px; text-align:right; font-weight:bold;">${diff > 0 ? '+' : ''}${diff.toFixed(2)}</td>
                                    </tr>
                                  `;
                            })()}
                                <tr style="border-top:1px solid #000;">
                                  <td colspan="${hasDiscount ? 9 : 8}" style="padding:8px; font-size:11px; font-style:italic">
                                    <b>${numberToWords(Math.round(netTotal))}</b>
                                  </td>
                                </tr>
                              ` : ''}
                            </tbody>
                          </table>
                        </div>

                        ${isLastPage ? `
                          <div style="border:1px solid #000; border-top:none; padding:8px;">
                            ${isIGST ? `
                              <table border="1" style="width:100%; border-collapse:collapse">
                                <tr>
                                  <th style="padding:4px; font-size:12px">HSN/SAC</th>
                                  <th style="padding:4px; font-size:12px">Taxable Value</th>
                                  <th style="padding:4px; font-size:12px">IGST Rate</th>
                                  <th style="padding:4px; font-size:12px">IGST Amt</th>
                                  <th style="padding:4px; font-size:12px">Total Tax Amt</th>
                                </tr>
                                ${hsnGroups.map(group => `
                                  <tr>
                                    <td style="padding:4px; font-size:12px">${String(group.hsnCode)}</td>
                                    <td style="padding:4px; font-size:12px">${money(group.taxableValue)}</td>
                                    <td style="padding:4px; font-size:12px">${Number(group.gstRate || 0)}%</td>
                                    <td style="padding:4px; font-size:12px">${money(group.igstAmt)}</td>
                                    <td style="padding:4px; font-size:12px">${money(group.igstAmt)}</td>
                                  </tr>
                                `).join('')}
                                <tr style="font-weight:bold">
                                  <td style="padding:4px; font-size:12px">Total</td>
                                  <td style="padding:4px; font-size:12px">${money(hsnGroups.reduce((sum, g) => sum + g.taxableValue, 0))}</td>
                                  <td style="padding:4px; font-size:12px"></td>
                                  <td style="padding:4px; font-size:12px">${money(hsnGroups.reduce((sum, g) => sum + g.igstAmt, 0))}</td>
                                  <td style="padding:4px; font-size:12px">${money(hsnGroups.reduce((sum, g) => sum + g.igstAmt, 0))}</td>
                                </tr>
                                <tr>
                                  <td colspan="5" style="padding:8px; font-size:11px; font-style:italic; background-color:#f9f9f9;">
                                    <b>${numberToWords(hsnGroups.reduce((sum, g) => sum + g.igstAmt, 0))}</b>
                                  </td>
                                </tr>
                              </table>
                            ` : `
                              <table border="1" style="width:100%; border-collapse:collapse">
                                <tr>
                                  <th style="padding:4px; font-size:12px">HSN/SAC</th>
                                  <th style="padding:4px; font-size:12px">Taxable Value</th>
                                  <th style="padding:4px; font-size:12px">CGST Rate</th>
                                  <th style="padding:4px; font-size:12px">CGST Amt</th>
                                  <th style="padding:4px; font-size:12px">SGST Rate</th>
                                  <th style="padding:4px; font-size:12px">SGST Amt</th>
                                  <th style="padding:4px; font-size:12px">Total Tax Amt</th>
                                </tr>
                                ${hsnGroups.map(group => {
                                const cgstRate = Number(group.gstRate || 0) / 2;
                                const sgstRate = Number(group.gstRate || 0) / 2;
                                return `
                                    <tr>
                                      <td style="padding:4px; font-size:12px">${String(group.hsnCode)}</td>
                                      <td style="padding:4px; font-size:12px">${money(group.taxableValue)}</td>
                                      <td style="padding:4px; font-size:12px">${cgstRate}%</td>
                                      <td style="padding:4px; font-size:12px">${money(group.cgstAmt)}</td>
                                      <td style="padding:4px; font-size:12px">${sgstRate}%</td>
                                      <td style="padding:4px; font-size:12px">${money(group.sgstAmt)}</td>
                                      <td style="padding:4px; font-size:12px">${money(group.cgstAmt + group.sgstAmt)}</td>
                                    </tr>
                                  `;
                            }).join('')}
                                <tr style="font-weight:bold">
                                  <td style="padding:4px; font-size:12px">Total</td>
                                  <td style="padding:4px; font-size:12px">${money(hsnGroups.reduce((sum, g) => sum + g.taxableValue, 0))}</td>
                                  <td style="padding:4px; font-size:12px"></td>
                                  <td style="padding:4px; font-size:12px">${money(hsnGroups.reduce((sum, g) => sum + g.cgstAmt, 0))}</td>
                                  <td style="padding:4px; font-size:12px"></td>
                                  <td style="padding:4px; font-size:12px">${money(hsnGroups.reduce((sum, g) => sum + g.sgstAmt, 0))}</td>
                                  <td style="padding:4px; font-size:12px">${money(hsnGroups.reduce((sum, g) => sum + g.cgstAmt + g.sgstAmt, 0))}</td>
                                </tr>
                                <tr>
                                  <td colspan="7" style="padding:8px; font-size:11px; font-style:italic; background-color:#f9f9f9;">
                                    <b>${numberToWords(hsnGroups.reduce((sum, g) => sum + g.cgstAmt + g.sgstAmt, 0))}</b>
                                  </td>
                                </tr>
                              </table>
                            `}
                          </div>

                          <div style="border:1px solid #000; border-top:none; padding:8px; display:flex; justify-content:space-between">
                            <div style="width:48%; border-right:1px solid #000; padding-right:10px;">
                              <b style="font-size:12px">Declaration</b><br/>
                              <span style="font-size:12px">We declare that this debit note shows the actual price of the goods described and that all particulars are true and correct.</span>
                            </div>
                            <div style="width:48%; padding-left:10px">
                              <b style="font-size:12px">Company's Bank Details</b><br/>
                              <span style="font-size:12px">Bank Name: <b>HDFC Bank C/C</b></span><br/>
                              <span style="font-size:12px">A/C No: <b>50200013650542</b></span><br/>
                              <span style="font-size:12px">Branch & IFSC: <b>Sevoke Road Branch & HDFC0000151</b></span>
                            </div>
                          </div>

                          <div style="border:1px solid #000; border-top:none; padding:8px; display:flex; justify-content:space-between">
                            <div style="font-size:12px">
                              <br/><br/><br/>
                              Customer Seal and Signature
                            </div>
                            <div style="text-align:right; font-size:12px">
                              <br/><br/><br/>
                              Authorised Signatory
                            </div>
                          </div>
                        ` : ''}
                      </div>
                    `;

                    container.innerHTML = pageHTML;

                    const canvas = await html2canvas(container, {
                        scale: 2,
                        useCORS: true,
                        logging: false,
                        backgroundColor: '#ffffff'
                    });

                    if (pageNum > 0) {
                        pdf.addPage();
                    }

                    const imgData = canvas.toDataURL('image/jpeg', 1.0);
                    const imgWidth = pageWidth - 2 * margin;
                    let imgHeight = (canvas.height * imgWidth) / canvas.width;

                    const maxHeight = pageHeight - 2 * margin;
                    if (imgHeight > maxHeight) {
                        imgHeight = maxHeight;
                    }

                    pdf.addImage(imgData, 'JPEG', margin, margin, imgWidth, imgHeight);
                }

                pdf.save(`debit-note-${dn.debitNoteNo || 'INV'}.pdf`);
            } finally {
                document.body.removeChild(container);
            }
        } catch (error) {
            console.error("Error generating PDF:", error);
            alert("Failed to download PDF. Please try again.");
        } finally {
            setDownloadingId(null);
        }
    };

    return (
        <div>
            <PageHeader
                pageName="VIEW DEBIT NOTES"
                breadcrump1="Purchase"
                breadcrump2="Debit Notes"
            />
            <div className="p-6">
                <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
                    <div className="flex justify-between items-center mb-6">
                        <h4 className="font-extrabold text-gray-800 flex items-center gap-2">
                            <FileText className="text-red-500" />
                            Debit Notes List
                        </h4>
                    </div>

                    {error && (
                        <Alert color="danger" className="mb-4">
                            <strong>Error:</strong> {error}
                        </Alert>
                    )}

                    {loading ? (
                        <div className="flex justify-center p-8">
                            <Spinner color="primary" />
                        </div>
                    ) : debitNotes.length === 0 ? (
                        <div className="text-center p-8 bg-gray-50 rounded-xl border border-gray-100">
                            <p className="text-gray-500 mb-0 font-medium">No Debit Notes created yet.</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto rounded-xl border border-gray-200">
                            <table className="w-full text-left align-middle whitespace-nowrap">
                                <thead className="bg-gray-100/50 text-[11px] uppercase tracking-widest text-gray-500 font-bold">
                                    <tr>
                                        <th className="p-4 border-b">Debit Note Info</th>
                                        <th className="p-4 border-b">PO Detail</th>
                                        <th className="p-4 border-b">Vendor Detail</th>
                                        <th className="p-4 border-b">Created</th>
                                        <th className="p-4 border-b text-center">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100 text-sm">
                                    {debitNotes.map(dn => (
                                        <tr key={dn._id} className="hover:bg-gray-50/50 transition-colors">
                                            <td className="p-4">
                                                <div className="font-bold text-gray-900 mb-1">{dn.debitNoteNo || 'N/A'}</div>
                                                <div className="text-xs text-gray-500">Inv: <span className="font-mono text-gray-700">{dn.invoiceNo || '—'}</span></div>
                                            </td>
                                            <td className="p-4">
                                                <div className="font-semibold text-gray-700">{dn.purchaseOrder?.poNumber || '—'}</div>
                                                <div className="text-xs text-gray-500 mt-1">{dn.lineItems?.length} Line Items returned</div>
                                            </td>
                                            <td className="p-4">
                                                <div className="font-bold text-blue-900">{dn.vendor?.vendorName || '—'}</div>
                                                <div className="text-xs text-gray-500 truncate max-w-[200px]" title={dn.vendor?.vendorEmail}>{dn.vendor?.vendorEmail || ''}</div>
                                            </td>
                                            <td className="p-4">
                                                <div className="font-semibold text-gray-700">{new Date(dn.createdAt).toLocaleDateString('en-GB')}</div>
                                                <div className="text-xs text-gray-400 mt-1">{dn.createdBy?.name || 'System'}</div>
                                            </td>
                                            <td className="p-4 text-center">
                                                <button
                                                    onClick={() => handleDownloadPdf(dn)}
                                                    disabled={downloadingId === dn._id}
                                                    className="bg-red-50 hover:bg-red-100 text-red-600 border border-red-200 px-3 py-1.5 rounded-lg text-xs font-bold transition-all shadow-sm flex items-center gap-2 mx-auto disabled:opacity-50"
                                                >
                                                    {downloadingId === dn._id ? <Spinner size="sm" /> : <Download size={14} />}
                                                    {downloadingId === dn._id ? 'Generating...' : 'Download PDF'}
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ViewDebitNotes;
